import { useEffect, useSyncExternalStore } from 'react';
import { touchScreen } from '@/lib/touch-screen';

// Full screen for the console (PO 2026-11-01: on an iPad, Safari's bars take a good part
// of the top of the screen — on Android too). The browser's Fullscreen API on the whole
// page (`<html>`), so the dialogs (portalled to <body>) come along; older iPadOS still
// names it `webkit…`. Only where the browser offers it — an iPad, Android, a desktop —
// never on an iPhone, whose Safari puts only a <video> in full screen: there the button
// and the question are simply not drawn. Going in needs the operator's own tap (call
// `enterFullscreen` from a click), which is why the console asks instead of switching.
//
// Safari on an iPad also leaves full screen whenever its keyboard comes up — WebKit
// refuses typing in a full-screen page, its guard against fake log-in screens — and no
// page can stay. So the console remembers that the operator chose full screen and goes
// back at their next tap outside a text field (`keepFullscreen`). Leaving on purpose —
// the button, Safari's own ✕ — is respected. Only an installed app (home screen) has
// no browser bars while typing (`installedApp`; install-prompt.tsx explains how) — and
// there the button is not drawn: there is nothing left to hide.

interface IWebkitDocument {
  webkitFullscreenEnabled?: boolean;
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
}

interface IFullscreenElement {
  requestFullscreen?: () => Promise<void>;
  webkitRequestFullscreen?: () => Promise<void> | void;
}

const EVENTS = ['fullscreenchange', 'webkitfullscreenchange'] as const;
/** How recently a text field took focus for a leave to count as the keyboard's doing. */
const KEYBOARD_MS = 2000;
/** Inputs that never bring up a keyboard. */
const NO_KEYBOARD = new Set(['button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit']);

/** The console's own way out is on its way: the next leave is on purpose. */
let leaving = false;

function doc(): Partial<Pick<Document, 'fullscreenEnabled' | 'fullscreenElement' | 'exitFullscreen'>> &
  IWebkitDocument {
  return document;
}

/** Opened from its icon on the home screen: no browser bars at all (manifest.webmanifest). */
export function installedApp(): boolean {
  if (typeof window === 'undefined') return false;
  const mode = (query: string): boolean =>
    typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  return (
    mode('(display-mode: standalone)') ||
    mode('(display-mode: fullscreen)') ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

/** Whether this browser can put the page in full screen. */
export function fullscreenSupported(): boolean {
  if (typeof document === 'undefined') return false;
  const d = doc();
  return !!(d.fullscreenEnabled ?? d.webkitFullscreenEnabled);
}

/** Whether the page is in full screen now. */
export function isFullscreen(): boolean {
  if (typeof document === 'undefined') return false;
  const d = doc();
  return !!(d.fullscreenElement ?? d.webkitFullscreenElement);
}

/** The console in full screen — from the operator's own tap. A refusal leaves it as it was. */
export async function enterFullscreen(): Promise<void> {
  const el: IFullscreenElement = document.documentElement;
  try {
    if (el.requestFullscreen) await el.requestFullscreen();
    else await el.webkitRequestFullscreen?.();
  } catch {
    // refused (no tap behind it, a browser policy): nothing to undo
  }
}

/** Back to the browser's own frame. */
export async function exitFullscreen(): Promise<void> {
  const d = doc();
  leaving = true;
  try {
    if (d.exitFullscreen) await d.exitFullscreen();
    else await d.webkitExitFullscreen?.();
  } catch {
    // already out (the operator swiped out first)
    leaving = false;
  }
}

/** A field that brings up a tablet's keyboard when it takes focus. */
export function typesText(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly;
  if (el instanceof HTMLInputElement) return !el.readOnly && !NO_KEYBOARD.has(el.type);
  return false;
}

/**
 * Back to full screen after the keyboard took it away (see the top of this file): on a
 * touch screen, a leave while a text field has (just had) focus is the keyboard's, and
 * the next tap outside a field goes back — in the tap, which the browser requires. Any
 * other leave is the operator's, and stands. Returns the uninstall.
 */
export function keepFullscreen(): () => void {
  leaving = false;
  let wanted = isFullscreen();
  let kicked = false;
  let typingAt = Number.NEGATIVE_INFINITY;
  const onFocus = (e: FocusEvent): void => {
    if (typesText(e.target)) typingAt = Date.now();
  };
  const onChange = (): void => {
    if (isFullscreen()) {
      wanted = true;
      kicked = false;
      return;
    }
    const keyboard = typesText(document.activeElement) || Date.now() - typingAt < KEYBOARD_MS;
    kicked = wanted && !leaving && touchScreen() && keyboard;
    wanted = kicked;
    leaving = false;
  };
  // `click`, not `pointerdown`: the tap's own action runs first, on the layout it saw.
  const onClick = (e: MouseEvent): void => {
    if (!kicked || isFullscreen() || typesText(e.target)) return;
    void enterFullscreen();
  };
  document.addEventListener('focusin', onFocus);
  document.addEventListener('click', onClick);
  for (const e of EVENTS) document.addEventListener(e, onChange);
  return () => {
    document.removeEventListener('focusin', onFocus);
    document.removeEventListener('click', onClick);
    for (const e of EVENTS) document.removeEventListener(e, onChange);
  };
}

/** `keepFullscreen` for as long as the calling component (the app shell) is mounted. */
export function useKeepFullscreen(): void {
  useEffect(() => keepFullscreen(), []);
}

function subscribe(onChange: () => void): () => void {
  for (const e of EVENTS) document.addEventListener(e, onChange);
  return () => {
    for (const e of EVENTS) document.removeEventListener(e, onChange);
  };
}

/** The page's full screen: whether the browser offers it, whether it is on, and the switch. */
export function useFullscreen(): { supported: boolean; active: boolean; toggle: () => void } {
  const active = useSyncExternalStore(subscribe, isFullscreen, () => false);
  return {
    supported: fullscreenSupported() && !installedApp(),
    active,
    toggle: () => void (active ? exitFullscreen() : enterFullscreen()),
  };
}
